import { Box, Select } from "@mantine/core";
import { useAtom, useSetAtom } from "jotai";
import { TFunction } from "react-i18next";

import Button from "components/Button";
import { YES_OR_NO_OPTIONS } from "components/ButtonGroup";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import {
  amicableSignedAtom,
  authoritiesInterventionAtom,
  driverPresentAtom,
  informationOnOtherVehicleAtom,
  numInjuredPeopleAtom,
  numVehiclesInvolvedAtom,
} from "atoms/accidentInformation";
import { authoritiesPhotosAtom } from "atoms/authoritiesPhotos";
import { caiPhotosAtom } from "atoms/caiPhotos";
import { injuredPeoplePhotosAtom } from "atoms/injuredPeoplePhotos";
import { DEFAULT_INJURED_PERSONS, DEFAULT_INJURED_PERSON_DATA, injuredPersonsAtom } from "atoms/injuredPersons";
import { firstNamePhAtom, lastNamePhAtom, personalCodePhAtom } from "atoms/phPersonalDetails";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { AuthoritiesIntervention, CaiEnum } from "types/DigitalJourneySession";

export const DJAccidentInformationPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [numVehiclesInvolved, setNumVehiclesInvolved] = useAtom(numVehiclesInvolvedAtom);
  const [driverPresent, setDriverPresent] = useAtom(driverPresentAtom);
  const [informationOnOtherVehicle, setInformationOnOtherVehicle] = useAtom(informationOnOtherVehicleAtom);
  const [numInjuredPeople, setNumInjuredPeople] = useAtom(numInjuredPeopleAtom);
  const [amicableSigned, setAmicableSigned] = useAtom(amicableSignedAtom);
  const [authoritiesIntervention, setAuthoritiesIntervention] = useAtom(authoritiesInterventionAtom);
  const setInjuredPeoplePhotos = useSetAtom(injuredPeoplePhotosAtom);
  const setInjuredPersons = useSetAtom(injuredPersonsAtom);
  const setCAIPhotos = useSetAtom(caiPhotosAtom);
  const setAuthoritiesPhotos = useSetAtom(authoritiesPhotosAtom);
  const setFirstNamePh = useSetAtom(firstNamePhAtom);
  const setLastNamePh = useSetAtom(lastNamePhAtom);
  const setPersonalCodePh = useSetAtom(personalCodePhAtom);

  const isDisabled =
    numVehiclesInvolved === null ||
    driverPresent === null ||
    (numVehiclesInvolved === 2 && informationOnOtherVehicle === null) ||
    numInjuredPeople === null ||
    amicableSigned === null ||
    authoritiesIntervention === null;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Accident Information - continue" });
    if (driverPresent === false) {
      setFirstNamePh("");
      setLastNamePh("");
      setPersonalCodePh("");
    }
    if (numInjuredPeople === 0) {
      setInjuredPeoplePhotos([]);
      setInjuredPersons(DEFAULT_INJURED_PERSONS);
    } else if (numInjuredPeople) {
      setInjuredPersons((injuredPersons) =>
        injuredPersons.map((person, index) => {
          if (index >= numInjuredPeople) {
            return { ...DEFAULT_INJURED_PERSON_DATA };
          }
          return person;
        })
      );
    }
    if (amicableSigned === CaiEnum.NOT_AVAILABLE) {
      setCAIPhotos([]);
    }
    if (authoritiesIntervention === AuthoritiesIntervention.NO) {
      setAuthoritiesPhotos([]);
    }
    onNext();
  };

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          {t("digital_journey.accident_information.title")}
        </Text>
        <Select
          name="numVehiclesInvolved"
          label={t("digital_journey.accident_information.vehicles_involved_question")}
          placeholder={t("digital_journey.accident_information.select")}
          data={getNumberOfVehiclesInvolved(t)}
          value={(numVehiclesInvolved ?? "").toString()}
          onChange={(value) => {
            setNumVehiclesInvolved(Number(value));
          }}
        />
        <Flex gap={8}>
          <Text size="md" weight={500}>
            {t("digital_journey.accident_information.driver_in_vehicle_question")}
          </Text>

          {getDriverPresentInVehicle(t).map((option) => (
            <Button
              aria-labelledby={`driverPresent-${option.label}-label`}
              aria-checked={driverPresent === option.value}
              role="radio"
              key={`driverPresent-${option.label}-label`}
              onClick={() => setDriverPresent(option.value)}
              typeVariant={driverPresent === option.value ? "primary" : "secondary"}
              sizeVariant="small"
              sx={{
                "& div": {
                  justifyContent: "flex-start",
                },
              }}
            >
              <label id={`driverPresent-${option.label}-label`}>{option.label}</label>
            </Button>
          ))}
        </Flex>

        {numVehiclesInvolved === 2 && (
          <Box>
            <Text weight={500} sizeVariant="md" mb={4}>
              {t("digital_journey.accident_information.plate_of_other_vehicle_question")}
            </Text>
            <Flex gap={16} direction="row" role="radiogroup" aria-labelledby="typeOfClaim">
              {YES_OR_NO_OPTIONS.map((option) => (
                <Button
                  aria-labelledby={`informationOnOtherVehicle-${t(option.label)}-label`}
                  aria-checked={informationOnOtherVehicle === option.value}
                  role="radio"
                  key={`informationOnOtherVehicle-${t(option.label)}-label`}
                  onClick={() => setInformationOnOtherVehicle(option.value)}
                  typeVariant={informationOnOtherVehicle === option.value ? "primary" : "secondary"}
                >
                  <label id={`informationOnOtherVehicle-${t(option.label)}-label`}>{t(option.label)}</label>
                </Button>
              ))}
            </Flex>
          </Box>
        )}
        <Select
          name="numInjuredPeople"
          label={t("digital_journey.accident_information.people_injured_question")}
          placeholder={t("digital_journey.accident_information.select")}
          data={getNumberOfPeopleInjured(t)}
          value={(numInjuredPeople ?? "").toString()}
          onChange={(value) => {
            setNumInjuredPeople(Number(value));
          }}
        />
        <Flex gap={8}>
          <Text size="md" weight={500}>
            {t("digital_journey.accident_information.signed_amicable_question")}
          </Text>
          {getAmicableDocSigned(t).map((option) => (
            <Button
              aria-labelledby={`amicableSigned-${option.label}-label`}
              aria-checked={amicableSigned === option.value}
              role="radio"
              key={`amicableSigned-${option.label}-label`}
              onClick={() => setAmicableSigned(option.value)}
              typeVariant={amicableSigned === option.value ? "primary" : "secondary"}
              sizeVariant="small"
              sx={{
                "& div": {
                  justifyContent: "flex-start",
                },
              }}
            >
              <label id={`amicableSigned-${option.label}-label`}>{option.label}</label>
            </Button>
          ))}
        </Flex>
        <Select
          name="authoritiesIntervention"
          label={t("digital_journey.accident_information.authorities_involved_question")}
          placeholder={t("digital_journey.accident_information.select")}
          data={getAuthoritiesInvolved(t)}
          value={authoritiesIntervention}
          onChange={(value) => setAuthoritiesIntervention(value as AuthoritiesIntervention)}
        />
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};

export const getNumberOfVehiclesInvolved = (t: TFunction) => {
  return [
    { label: t("digital_journey.accident_information.two_vehicles_involved"), value: "2" },
    { label: t("digital_journey.accident_information.one_vehicle_involved"), value: "1" },
    { label: t("digital_journey.accident_information.more_vehicles_involved"), value: "3" },
  ];
};

export const getDriverPresentInVehicle = (t: TFunction) => {
  return [
    { label: t("digital_journey.accident_information.driver_in_vehicle_yes"), value: true },
    { label: t("digital_journey.accident_information.driver_in_vehicle_no"), value: false },
  ];
};

export const getNumberOfPeopleInjured = (t: TFunction) => {
  return [
    { label: t("digital_journey.accident_information.people_injured_none"), value: "0" },
    { label: t("digital_journey.accident_information.people_injured_one"), value: "1" },
    { label: t("digital_journey.accident_information.people_injured_two"), value: "2" },
    { label: t("digital_journey.accident_information.people_injured_three"), value: "3" },
    { label: t("digital_journey.accident_information.people_injured_four"), value: "4" },
    { label: t("digital_journey.accident_information.people_injured_five"), value: "5" },
    { label: t("digital_journey.accident_information.people_injured_more_than_five"), value: "5" },
  ];
};

export const getAmicableDocSigned = (t: TFunction) => {
  return [
    { label: t("digital_journey.accident_information.signed_amicable_only_me"), value: CaiEnum.ONE_SIGNED },
    { label: t("digital_journey.accident_information.signed_amicable_both_drivers"), value: CaiEnum.BOTH_SIGNED },
    { label: t("digital_journey.accident_information.signed_amicable_no"), value: CaiEnum.NOT_AVAILABLE },
  ];
};

export const getAuthoritiesInvolved = (t: TFunction) => {
  return [
    { label: t("digital_journey.accident_information.authorities_involved_no"), value: AuthoritiesIntervention.NO },
    {
      label: t("digital_journey.accident_information.authorities_involved_yes_police"),
      value: AuthoritiesIntervention.POLICE,
    },
    {
      label: t("digital_journey.accident_information.authorities_involved_yes_local_police"),
      value: AuthoritiesIntervention.LOCAL_POLICE,
    },
    {
      label: t("digital_journey.accident_information.authorities_involved_yes_city_police"),
      value: AuthoritiesIntervention.CITY_POLICE,
    },
    {
      label: t("digital_journey.accident_information.authorities_involved_yes_carabinieri"),
      value: AuthoritiesIntervention.CARABINIERI,
    },
    {
      label: t("digital_journey.accident_information.authorities_involved_yes_firefighters"),
      value: AuthoritiesIntervention.FIREFIGHTERS,
    },
    {
      label: t("digital_journey.accident_information.authorities_involved_yes_other_police"),
      value: AuthoritiesIntervention.OTHER_POLICE,
    },
  ];
};
